<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import 'leaflet/dist/leaflet.css'
import '@/assets/styles/map/styles.css'

import { useElectionStore } from '../../election/stores/election-map.store'
import { useConstituencyVoterStore } from '../../voter/stores/constituency-voter.store'
import { useMapUiStore } from '@/modules/map/stores/ui.store'
import { useConstituencyMapStore } from '../stores/constituency-map.store'

import MapHeader from '../../map/components/MapHeader.vue'
import Legend from '../../map/components/Legend.vue'
import ElectionSummaryPollingStation from '@/modules/election/components/ElectionSummaryPollingStation.vue'
import type ConstituencyAgeGroupModel from '@/modules/voter/models/constituency-age-group.model'

const mapContainer = ref<HTMLElement | null>(null)
const electionStore = useElectionStore()
const voterStore = useConstituencyVoterStore()
const mapUiStore = useMapUiStore()
const constituencyMapStore = useConstituencyMapStore()

function resetStore() {
  electionStore.resetStore()
  voterStore.resetStore()
  mapUiStore.resetStore()
  constituencyMapStore.resetStore()
}

onMounted(async () => {
  await Promise.all([
    constituencyMapStore.getConstituencyShapeFile('akwatia'),
    electionStore.getParliamentaryElections(),
    voterStore.getConstituencyVoters('akwatia'),
    voterStore.getVoterCounts()
  ])
  if (mapContainer.value) {
    constituencyMapStore.initializeMap(mapContainer.value)
    constituencyMapStore.initializeLayers()
  }
})

const mapHeader = computed(() => {
  const year = electionStore.selectedParliamentaryElection.year
  return `CONSTITUENCY PARLIAMENTARY RESULTS MAP ${year}`
})

const ageGroups = ref(['18_30', '31_40', '41_50', '51_60', '61_100', '101plus'])

// Compute aggregated data with filters applied
const aggregated = computed(() => {
  return voterStore.aggregateStations()
})

const ageGroupData = computed(() => {
  return Object.keys(aggregated.value.voters).map(ageGroup => ({
    ageGroup: ageGroup.replace('_', '-'),
    total: aggregated.value.voters[ageGroup as keyof ConstituencyAgeGroupModel],
    male: aggregated.value.votersMale[ageGroup as keyof ConstituencyAgeGroupModel],
    female: aggregated.value.votersFemale[ageGroup as keyof ConstituencyAgeGroupModel]
  }));
});

</script>

<template>
  <div class="parent">
    <div class="parliamentary-container">
      <MapHeader :title="mapHeader" />
      <div class="parliamentary">
        <TabView class="map-statistics" v-model:activeIndex="constituencyMapStore.activeTabIndex">
          <TabPanel header="Result">
            <div>
              <div class="map-statistics-container">
                <div class="map-statistics-content">
                  <ElectionSummaryPollingStation
                    :year="electionStore.selectedParliamentaryElection.year"
                    :summary="electionStore.parliamentaryElectionPollingStationResult"
                  />
                </div>
                <div class="map-statistics-legend">
                  <Button class="control-button" @click="constituencyMapStore.toggleLegend" text>
                    {{ constituencyMapStore.isLegendVisible ? 'Hide Legend' : 'Show Legend' }}
                  </Button>
                  <Legend
                    v-if="constituencyMapStore.isLegendVisible"
                    title="Win Margin"
                    :items="constituencyMapStore.legendItems"
                    :horizontal="true"
                  />
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel header="Demographics">
            <div class="grid">
              <!-- Summary Cards -->
              <div class="col-12">
                <div class="grid">
                  <div class="col-12 md:col-6 lg:col-3">
                    <Card class="bg-blue-50">
                      <template #title>Total Stations</template>
                      <template #content>
                        <div class="text-4xl font-bold">{{ aggregated.totalStations }}</div>
                      </template>
                    </Card>
                  </div>
                  <div class="col-12 md:col-6 lg:col-3">
                    <Card class="bg-green-50">
                      <template #title>Total Voters</template>
                      <template #content>
                        <div class="text-4xl font-bold">
                          {{ aggregated.totalVoters.toLocaleString() }}
                        </div>
                      </template>
                    </Card>
                  </div>
                  <div class="col-12 md:col-6 lg:col-3">
                    <Card class="bg-cyan-50">
                      <template #title>Male Voters</template>
                      <template #content>
                        <div class="text-4xl font-bold">
                          {{ aggregated.totalVotersMale.toLocaleString() }}
                        </div>
                        <div>
                          {{
                            ((aggregated.totalVotersMale / aggregated.totalVoters) * 100).toFixed(
                              1
                            )
                          }}%
                        </div>
                      </template>
                    </Card>
                  </div>
                  <div class="col-12 md:col-6 lg:col-3">
                    <Card class="bg-pink-50">
                      <template #title>Female Voters</template>
                      <template #content>
                        <div class="text-4xl font-bold">
                          {{ aggregated.totalVotersFemale.toLocaleString() }}
                        </div>
                        <div>
                          {{
                            ((aggregated.totalVotersFemale / aggregated.totalVoters) * 100).toFixed(
                              1
                            )
                          }}%
                        </div>
                      </template>
                    </Card>
                  </div>
                </div>
              </div>
            </div>

            <!-- Age Group Breakdown Table -->
            <div class="col-12">
              <Card>
                <template #title>Detailed Age Group Breakdown</template>
                <template #content>
                  <DataTable :value="ageGroupData" :autoLayout="true">
                    <Column field="ageGroup" header="Age Group"></Column>
                    <Column field="total" header="Total Voters">
                      <template #body="{data}">
                        {{ data.total.toLocaleString() }}
                        ({{ ((data.total / aggregated.totalVoters) * 100).toFixed(1) }}%)
                      </template>
                    </Column>
                    <Column field="male" header="Male Voters">
                      <template #body="{data}">
                        {{ data.male.toLocaleString() }}
                        ({{ ((data.male / aggregated.totalVotersMale) * 100).toFixed(1) }}%)
                      </template>
                    </Column>
                    <Column field="female" header="Female Voters">
                      <template #body="{data}">
                        {{ data.female.toLocaleString() }}
                        ({{ ((data.female / aggregated.totalVotersFemale) * 100).toFixed(1) }}%)
                      </template>
                    </Column>
                  </DataTable>
                </template>
              </Card>
            </div>
          </TabPanel>
        </TabView>

        <div class="map-content">
          <div class="map-controls">
            <div class="map-controls-left">
              <Dropdown
                v-model="electionStore.selectedParliamentaryElection"
                :options="electionStore.parliamentaryElections"
                optionLabel="year"
                placeholder="Select Year"
                checkmark
                :highlightOnSelect="true"
              />
              <MultiSelect
                v-model="constituencyMapStore.selectedPollingStations"
                :options="constituencyMapStore.pollingStations"
                filter
                optionLabel="name"
                placeholder="Select Polling Stations"
                :maxSelectedLabels="2"
                class="w-full md:w-18rem"
              >
                <template #footer>
                  <div class="py-2 px-3">
                    <b>{{
                      constituencyMapStore.selectedPollingStations
                        ? constituencyMapStore.selectedPollingStations.length
                        : 0
                    }}</b>
                    item{{
                      (constituencyMapStore.selectedPollingStations
                        ? constituencyMapStore.selectedPollingStations.length
                        : 0) > 1
                        ? 's'
                        : ''
                    }}
                    selected.
                  </div>
                </template>
              </MultiSelect>

              <Button @click="constituencyMapStore.fitBounds" text>Resize Map</Button>
              <Button @click="constituencyMapStore.toggleColorMode" text>Toggle Color</Button>
              <Button @click="resetStore" text>Reset</Button>
            </div>
          </div>
          <div ref="mapContainer" class="map-container"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Container for each child and its heading */
.presidential-container,
.parliamentary-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
}

.map-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
}

.map-controls-left {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.map-controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
